import { create } from 'zustand';
import type { NodeKey, NodeState, ReviewPayload, Toast, Upload, Workflow } from '@/lib/types';
import { NODE_ORDER } from '@/lib/types';

const idle = (): Record<NodeKey, NodeState> => {
  const m = {} as Record<NodeKey, NodeState>;
  for (const k of NODE_ORDER) m[k] = { kind: 'idle' };
  return m;
};

const rid = () => Math.random().toString(36).slice(2, 10);

function newWorkflow(): Workflow {
  return {
    id: rid(),
    streamerUrl: '',
    streamerHandle: undefined,
    tracking: false,
    autoUpload: false,
    editPrompt: '',
    states: idle(),
    uploads: [],
    pendingReview: null,
    createdAt: Date.now()
  };
}

interface State {
  workflows: Workflow[];
  toasts: Toast[];
  addWorkflow: () => string;
  removeWorkflow: (id: string) => void;
  setStreamerUrl: (id: string, url: string) => void;
  setTracking: (id: string, tracking: boolean, handle?: string) => void;
  setAutoUpload: (id: string, auto: boolean) => void;
  setEditPrompt: (id: string, prompt: string) => void;
  setNodeState: (id: string, key: NodeKey, state: NodeState) => void;
  pushUpload: (id: string, upload: Upload) => void;
  setPendingReview: (id: string, payload: ReviewPayload | null) => void;
  pushToast: (message: string, workflowId?: string) => void;
  dismissToast: (id: string) => void;
}

export const useWorkflows = create<State>((set) => ({
  workflows: [newWorkflow()],
  toasts: [],

  addWorkflow: () => {
    const wf = newWorkflow();
    set((s) => ({ workflows: [...s.workflows, wf] }));
    return wf.id;
  },

  removeWorkflow: (id) =>
    set((s) => ({
      workflows: s.workflows.filter((w) => w.id !== id) || []
    })),

  setStreamerUrl: (id, url) =>
    set((s) => ({
      workflows: s.workflows.map((w) => (w.id === id ? { ...w, streamerUrl: url } : w))
    })),

  setTracking: (id, tracking, handle) =>
    set((s) => ({
      workflows: s.workflows.map((w) =>
        w.id === id
          ? {
              ...w,
              tracking,
              streamerHandle: handle ?? w.streamerHandle,
              states: {
                ...w.states,
                source: tracking ? { kind: 'active', label: 'tracking' } : { kind: 'idle' }
              }
            }
          : w
      )
    })),

  setAutoUpload: (id, auto) =>
    set((s) => ({ workflows: s.workflows.map((w) => (w.id === id ? { ...w, autoUpload: auto } : w)) })),

  setEditPrompt: (id, prompt) =>
    set((s) => ({ workflows: s.workflows.map((w) => (w.id === id ? { ...w, editPrompt: prompt } : w)) })),

  setNodeState: (id, key, nodeState) =>
    set((s) => ({
      workflows: s.workflows.map((w) =>
        w.id === id ? { ...w, states: { ...w.states, [key]: nodeState } } : w
      )
    })),

  pushUpload: (id, upload) =>
    set((s) => ({
      workflows: s.workflows.map((w) =>
        w.id === id ? { ...w, uploads: [...w.uploads, upload] } : w
      )
    })),

  setPendingReview: (id, payload) =>
    set((s) => ({
      workflows: s.workflows.map((w) => (w.id === id ? { ...w, pendingReview: payload } : w))
    })),

  pushToast: (message, workflowId) =>
    set((s) => ({
      toasts: [...s.toasts, { id: rid(), message, workflowId, createdAt: Date.now() }]
    })),

  dismissToast: (id) => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) }))
}));

export function workflowSelector(id: string) {
  return (s: State) => s.workflows.find((w) => w.id === id);
}
